import addPerfMetricToPageTimings from 'mini-performance/lib/utils/addPerfMetricToPageTimings';
import { type DatapointVariant } from 'owa-analytics-types';
import { getScenarioData } from 'mini-host-bridge/lib/utils/getScenarioData';
import { getSessionId } from 'owa-config';
import { getUserConfiguration } from 'owa-session-store';
import { isMiniHostAppFeatureEnabled } from 'mini-hostapp-feature-flags/lib/isMiniHostAppFeatureEnabled';
import { parseOutlookLiteLsrData } from './utils/parseOutlookLiteNativeLsrData';
import { PerformanceDatapoint } from 'owa-analytics';
import { sendLSRMessage } from 'mini-host-bridge/lib/sendLSRMessage';
import type { LiteScenarioData } from 'mini-host-bridge/lib/types/LiteScenarioData';

const LSR_DATA_POINT_NAME = 'LargestSurfaceRender';

/**
 * Log the performance measure
 * @param perfMeasure The PerformanceEntry to log
 * @param name Datapoint name to log. If not specified, uses the performance measure name
 * @param logOnce If true, logs only the first time the datapoint is fired
 * @param addToPageTimings If true, adds the perf metrics to global _pageTimings object
 * @param markAsCore If true, marks the datapoint as core event so that sampling is turned off for the event
 */
export async function logPerfMeasure(
    perfMeasure: PerformanceEntry,
    name?: string,
    logOnce?: boolean,
    addToPageTimings?: boolean,
    markAsCore?: boolean,
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
     * See aka.ms/mini-lint-errors
     *	> Unexpected any. Specify a different type. */
    customPayload?: any
) {
    if (perfMeasure) {
        // Hosted scenario for OPX/Native
        const { startTime, duration } = perfMeasure;
        const endTime = Math.round(startTime + duration);

        if (isMiniHostAppFeatureEnabled('nativePerfLog') && name === LSR_DATA_POINT_NAME) {
            const hostLoadMeasure = await getHostLoadMeasures();
            Object.assign(customPayload, hostLoadMeasure);

            sendMessageToHost(endTime, customPayload);
        }

        const measureName = name || perfMeasure.name;
        const options = { isCore: !!markAsCore };

        const perfDataPoint = new PerformanceDatapoint(
            measureName,
            logOnce ? { ...options, variant: 2 } : options
        );
        perfDataPoint.addCustomData({ duration, ...customPayload });
        perfDataPoint.end(endTime);

        if (addToPageTimings) {
            addPerfMetricToPageTimings(measureName, endTime);
        }
    }
}

const getHostLoadMeasures = async () => {
    try {
        const scenarioData = await getScenarioData<LiteScenarioData>();
        if (scenarioData?.lsr) {
            const { appVersionCode, appVersionName, lsr, deviceRamInMb } = scenarioData;

            // prefixing native props with n_
            return {
                n_avc: appVersionCode,
                n_avn: appVersionName,
                n_ram: deviceRamInMb,
                ...parseOutlookLiteLsrData(lsr),
            };
        }
        return {};
    } catch (error) {
        return {};
    }
};

/* eslint-disable-next-line @typescript-eslint/no-explicit-any --
 * See aka.ms/mini-lint-errors
 *	> Unexpected any. Specify a different type. */
const isNotUndefined = (value: any) => value !== undefined;

/* eslint-disable-next-line @typescript-eslint/no-explicit-any --
 * See aka.ms/mini-lint-errors
 *	> Unexpected any. Specify a different type. */
const sendMessageToHost = (miniLSR: number, customData: any) => {
    const {
        mlt,
        c_psd = 0,
        c_em = 0,
        n_wr = 0,
        iph,
        wph,
        nbt,
        mnbt,
        werts,
        werdbs,
        beforePaint = 0,
        rtt,
        et = 'NA',
        bt_sd,
        bt_btsp,
        bt_ren,
        in_e,
    } = customData;

    const e2e = miniLSR + n_wr;
    const canComputeCacheStatus = isNotUndefined(werts) && isNotUndefined(werdbs);
    const isSWBoot = mnbt === 'SwCache';

    let cacheStatusByResourceTiming = 'NotAvailable';
    if (canComputeCacheStatus) {
        if (parseInt(werts) === 0 && parseInt(werdbs) >= 0) {
            cacheStatusByResourceTiming = 'Yes';
        } else {
            cacheStatusByResourceTiming = 'No';
        }
    }

    let isCached = 'NA';
    if (cacheStatusByResourceTiming === 'Yes' || isSWBoot) {
        isCached = 'Y';
    } else if (cacheStatusByResourceTiming === 'No') {
        isCached = 'N';
    } else if (mnbt === 'NoCache') {
        isCached = 'N';
    }

    const attributes = [
        e2e,
        miniLSR,
        mlt,
        c_psd,
        iph,
        wph,
        nbt,
        mnbt,
        isCached,
        rtt,
        et,
        in_e,
        bt_sd,
        bt_btsp,
        bt_ren,
        beforePaint,
        c_em,
    ];

    sendLSRMessage({
        sessionId: getSessionId(),
        puid: getUserConfiguration()?.SessionSettings?.UserPuid,
        miniLSR,
        loadMetrics: attributes.join('|'),
    });
};
